/**
 * The desktop's sound scheme: the real Windows XP sounds, served as ordinary
 * MP3s from `public/assets/sounds/` and played through one long-lived
 * `<audio>` element per file.
 *
 * A kept element rather than one per playback is deliberate. These are all
 * short, none of them is ever meant to overlap with itself, and re-winding a
 * live element is both cheaper than constructing one and the only way to keep
 * a rapid double-trigger from stacking into a flam.
 *
 * Browsers block audio until the user gestures, so `unlock()` is wired to the
 * first pointerdown and the startup chime is deliberately played from the
 * click on the Welcome screen user tile — that click *is* the gesture.
 */

/**
 * Named for the Win32 system event, not for the file — because in XP the two
 * do not line up. `SystemAsterisk`, the sound an information box makes, ships
 * as `Windows XP Error.wav`, while the file called `Windows XP Ding.wav` is
 * the plain Default Beep. Going by filename here would silently swap them.
 */
export type SoundName =
  | 'startup'
  | 'shutdown'
  | 'logoff'
  | 'ding'
  | 'asterisk'
  | 'error'
  | 'exclamation'
  | 'balloon'
  | 'recycle';

const DIR = '/assets/sounds/';

const FILES: Record<SoundName, string> = {
  startup: 'Windows XP Startup.mp3',
  shutdown: 'Windows XP Shutdown.mp3',
  logoff: 'Windows XP Logoff Sound.mp3',
  // Default Beep — a message box with no icon at all.
  ding: 'Windows XP Ding.mp3',
  // SystemAsterisk. The file name is a red herring: this is the mild chime an
  // *information* box makes, not the sound of anything going wrong.
  asterisk: 'Windows XP Error.mp3',
  // SystemHand, which the Sounds control panel listed as "Critical Stop".
  error: 'Windows XP Critical Stop.mp3',
  exclamation: 'Windows XP Exclamation.mp3',
  // XP's tray balloon had a sound of its own (`Windows XP Balloon.wav`) that
  // isn't in the set here. Ding is the nearest thing in character; drop the
  // real file in and change this one line if it ever turns up.
  balloon: 'Windows XP Ding.mp3',
  recycle: 'Windows XP Recycle.mp3',
};

/**
 * Five more of the scheme's files sit in `public/assets/sounds/` with no name
 * above, and that is deliberate rather than an oversight. Each belongs to a
 * Win32 event this desktop does not have, and inventing a trigger for one
 * would be less authentic than the silence:
 *
 * | File                          | Win32 event        | Why it is silent here   |
 * | ----------------------------- | ------------------ | ----------------------- |
 * | Windows XP Logon Sound.mp3    | WindowsLogon       | `startup` already covers |
 * |                               |                    | the tile click — the one |
 * |                               |                    | gesture audio can unlock |
 * | Windows XP Start.mp3          | Start Navigation   | needs an IE nav hook     |
 * | Windows XP Restore.mp3        | RestoreUp/Down     | would fire on every      |
 * |                               |                    | taskbar click            |
 * | Windows XP Hardware Insert    | DeviceConnect      | no removable devices     |
 * | Windows XP Hardware Remove    | DeviceDisconnect   | no removable devices     |
 *
 * They stay on disk because nothing in `public/` is fetched until something
 * asks for it, so an unmapped file costs a visitor nothing — and re-sourcing
 * one later is harder than keeping it.
 */

/**
 * The scheme is mastered a good deal hotter than a synthesised chime, and the
 * startup sound is the loudest thing in it. Half volume puts it where a
 * desktop notification belongs rather than where a media player does.
 */
const VOLUME = 0.5;

let enabled = true;
/**
 * Keyed by *file*, not by event: two events that name the same file are the
 * same sound, and giving them an element each would fetch the file twice and
 * let them talk over one another.
 */
const cache = new Map<string, HTMLAudioElement>();

function supported(): boolean {
  return typeof window !== 'undefined' && typeof window.Audio === 'function';
}

function element(file: string): HTMLAudioElement | null {
  if (!supported()) return null;
  const cached = cache.get(file);
  if (cached) return cached;
  try {
    const el = new Audio(DIR + encodeURIComponent(file));
    el.preload = 'auto';
    el.volume = VOLUME;
    cache.set(file, el);
    return el;
  } catch {
    return null;
  }
}

/**
 * Call from a user-gesture handler so later programmatic sounds are allowed.
 *
 * The gesture is also the first honest opportunity to fetch, and constructing
 * an element with `preload="auto"` is itself the request — so building them
 * all here is the whole of the warm-up, and the startup chime isn't left
 * waiting on the network at the moment it is asked to sound.
 */
export function unlock(): void {
  if (!supported()) return;
  for (const file of new Set(Object.values(FILES))) element(file);
}

export function setSoundEnabled(value: boolean): void {
  enabled = value;
  if (!value) {
    for (const el of cache.values()) el.pause();
  }
}

export function isSoundEnabled(): boolean {
  return enabled;
}

export function play(name: SoundName): void {
  if (!enabled) return;
  const el = element(FILES[name]);
  if (!el) return;
  try {
    // Re-wind rather than let a second trigger be swallowed: `play()` on an
    // element that is already playing is a no-op, which would silently drop
    // the second of two message boxes opened back to back.
    el.currentTime = 0;
    const started = el.play();
    // Autoplay refusals reject, and jsdom's stub returns undefined outright.
    // Neither is worth an unhandled rejection.
    if (started && typeof started.catch === 'function') started.catch(() => undefined);
  } catch {
    /* No audio device, or the element is in a state that refuses playback. */
  }
}

/** Attach the one-time gesture listener that unlocks audio. */
export function installUnlockListener(): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => unlock();
  window.addEventListener('pointerdown', handler, { once: true, passive: true });
  window.addEventListener('keydown', handler, { once: true });
  return () => {
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
  };
}
